/**
 * #3355 — the words that name who serves this client, in the tenant's branding.
 *
 * <p>A white-label partner's clients see the partner, a direct client sees us: the tenant's
 * `branding` block rides on `/me` (`tenant_branding`) and, when it names a vendor, every "your …
 * contact" sentence, the powered-by line and the tab title name that vendor instead. With no
 * branding — and for any viewer who is a platform admin or can switch tenant, exactly as the theme
 * is not worn for them (theme/tenantBrand.ts) — every string is the house string, verbatim.</p>
 */
import { useMemo } from "react";
import { useMe } from "../api/portalQueries.js";
import { canSwitchTenant } from "../api/tenantSelection.js";
import type { MeResponse } from "../api/types.js";

/** The house contact phrase every "speak to / contact …" sentence uses. */
export const HOUSE_CONTACT = "your AR Dept contact";

/** The house powered-by line under a branded nav. */
export const HOUSE_POWERED_BY = "Powered by The AR Dept.";

/** What the portal's vendor-naming strings say for this viewer. */
export interface VendorCopy {
  /** "your AR Dept contact", or "your <vendor> contact". */
  contact: string;
  /** "Powered by The AR Dept.", or "Powered by <vendor>." */
  poweredBy: string;
  /**
   * The tab title's product half when the tenant is not known: the tenant's `console_title`, else
   * "<vendor> reports" when it names a vendor, else null (the house title stands).
   */
  title: string | null;
}

/** The house strings. */
export const HOUSE_VENDOR_COPY: VendorCopy = {
  contact: HOUSE_CONTACT,
  poweredBy: HOUSE_POWERED_BY,
  title: null,
};

/** The vendor copy for this `/me`. Pure. */
export function vendorCopy(me: MeResponse | undefined): VendorCopy {
  const branding = me?.tenant_branding;
  if (!me || !branding) return HOUSE_VENDOR_COPY;
  if (me.is_platform_admin || canSwitchTenant(me)) return HOUSE_VENDOR_COPY;

  const vendor = branding.vendor_name?.trim() || null;
  const consoleTitle = branding.console_title?.trim() || null;
  return {
    contact: vendor ? `your ${vendor} contact` : HOUSE_CONTACT,
    poweredBy: vendor ? `Powered by ${vendor}.` : HOUSE_POWERED_BY,
    title: consoleTitle ?? (vendor ? `${vendor} reports` : null),
  };
}

/** {@link vendorCopy} for the signed-in viewer (one shared `/me` read). */
export function useVendorCopy(): VendorCopy {
  const me = useMe();
  return useMemo(() => vendorCopy(me.data), [me.data]);
}
